/* eslint-disable camelcase */
// @ts-check
import { createLog } from '../utils/logger'
import { TCString } from '@mailonline/tcf-core'
import { isTCFVersion2_2 } from '../utils/policyVersion'
import { getNrvVersions } from './nrvVersions'
const log = createLog()

/**
 * @param {number} tcfPolicyVersion
 * @returns {2|3}
 */
function gvlSpecForTcfPolicy (tcfPolicyVersion) {
  return isTCFVersion2_2(tcfPolicyVersion) ? 3 : 2
}

/**
 * @param {Array<string>} clientGVLUIVersionParts
 * @returns {boolean}
 */
const isClientGVLVersionGVLSpec3OrAbove = (clientGVLUIVersionParts) =>
  clientGVLUIVersionParts.length === 2

/**
 * @param {number} gvlSpecificationVersion
 * @param {string} clientGvlSpecVersion
 * @returns {boolean}
 */
const gvlUiVersionAndTcStringGVLSpecMatch = (
  gvlSpecificationVersion,
  clientGvlSpecVersion
) => `v${gvlSpecificationVersion}` === clientGvlSpecVersion
/**
 * @param {string|undefined} tcString
 * @param {string|number} clientGvlUiVersion
 * @return {{gvlVersion: string, gvlUiVersion: string}}
 */
function getGVLVersions(tcString, clientGvlUiVersion) {
  if (!tcString) {
    log('no tcString, returning configured gvlUiVersion', clientGvlUiVersion)
    return {
      gvlVersion: `${clientGvlUiVersion}`,
      gvlUiVersion: `${clientGvlUiVersion}`
    }
  }

  /** @type {TCModel} */
  const tcModel = TCString.decode(tcString)
  const { vendorListVersion } = tcModel
  const tcStringTCFPolicyVersion = Number(tcModel.policyVersion)
  const gvlSpecificationVersion = gvlSpecForTcfPolicy(tcStringTCFPolicyVersion)
  const gvlVersion = parseInt(vendorListVersion.toString())

  log('gvlVersion from tcString', gvlVersion)

  const clientGvlUiVersionParts = clientGvlUiVersion.toString().split('/')

  if (isClientGVLVersionGVLSpec3OrAbove(clientGvlUiVersionParts)) {
    const [clientGvlSpecVersion, clientGvlVersion] = clientGvlUiVersionParts
    const gvlUiVersion = clientGvlVersion === 'latest' ? 'latest' : Math.max(gvlVersion, Number(clientGvlVersion))

    return gvlUiVersionAndTcStringGVLSpecMatch(
      gvlSpecificationVersion,
      clientGvlSpecVersion
    )
      ? {
          gvlVersion: `v${gvlSpecificationVersion}/${gvlVersion}`,
          gvlUiVersion: `v${gvlSpecificationVersion}/${gvlUiVersion}`
        }
      : {
          gvlVersion: isTCFVersion2_2(tcStringTCFPolicyVersion)
            ? `v3/${gvlVersion}`
            : `${gvlVersion}`,
          gvlUiVersion: `${clientGvlUiVersion}`
        }
  } else {
    return isTCFVersion2_2(tcStringTCFPolicyVersion)
      ? {
          gvlVersion: `v3/${gvlVersion}`,
          gvlUiVersion: `${clientGvlUiVersion}`
        }
      : {
          gvlVersion: `${gvlVersion}`,
          gvlUiVersion: `${clientGvlUiVersion === 'latest' ? 'latest' : Math.max(gvlVersion, Number(clientGvlUiVersion))}`
        }
  }
}

/**
 * @param {string|number|undefined} _cvlVersion
 * @param {string|number} cvlUiVersionFromClient
 * @return {{cvlVersion: string|number, cvlUiVersion: number}}
 */
function getCvlVersions(_cvlVersion, cvlUiVersionFromClient) {
  let cvlUiVersion = Number(cvlUiVersionFromClient)
  /** @type {string|number} */
  let cvlVersion
  // this is only necessary because the initial version '0.0.1', all following versions are integers
  if (_cvlVersion && !Number(_cvlVersion)) {
    cvlVersion = _cvlVersion
  } else if (cvlUiVersionFromClient && !Number(cvlUiVersionFromClient)) {
    cvlVersion = cvlUiVersionFromClient
  } else {
    cvlVersion = _cvlVersion
      ? Number(_cvlVersion)
      : cvlUiVersionFromClient
        ? Number(cvlUiVersionFromClient)
        : cvlUiVersion
    cvlUiVersion = cvlVersion
      ? Math.max(cvlVersion, cvlUiVersion)
      : cvlUiVersion
  }
  log('selected cvlVersions', { cvlVersion, cvlUiVersion })
  return { cvlVersion, cvlUiVersion }
}

/**
 * @param {OverrideableByStoredConfigItems} selectedTCDataItems
 * @param {ClientConfig} clientConfig
 * @return {{gvlVersion: string, gvlUiVersion: string, nrvVersion: string, nrvUiVersion: string, cvlVersion: string|number, cvlUiVersion: number}}
 */
function getVendorListVersions(selectedTCDataItems, clientConfig) {
  const { tcString, nrvVersion, cvlVersion } = selectedTCDataItems
  const { gvlUiVersion, nrvUiVersion, cvlUiVersion } = clientConfig
  return {
    ...getGVLVersions(tcString, gvlUiVersion),
    ...getNrvVersions(nrvVersion, nrvUiVersion.toString()),
    ...getCvlVersions(cvlVersion, cvlUiVersion)
  }
}

const setGvlVersionByMolGvlVersion =
  /** @type {(molGvlVersion: string) => string} */
  (molGvlVersion) => molGvlVersion?.replace(/\./g, '/')

const setGvlVersionByClientConfig =
  /** @type {(version: string, geo: string|undefined, gvlEnvironment: string|undefined) => string } */
  (version, geo, gvlEnvironment) =>
    geo && gvlEnvironment ? `${version}/${geo}/${gvlEnvironment}` : version

/**
 * @param {ClientConfig} clientConfig
 * @param {string|undefined} molGvlVersion
 * @param {boolean} existingUser
 * @param {string} _gvlVersion
 * @param {string} _gvlUiVersion
 * @returns {{gvlVersion: number|string, gvlUiVersion: number|string, migratedGvlVersion: string|undefined, migrateUserGvl: boolean}}
 **/
function applyMigrationConfig(
  clientConfig,
  molGvlVersion = '',
  existingUser,
  _gvlVersion,
  _gvlUiVersion
) {
  const newUser = !clientConfig.tcString

  const gvlSpec2MigrationPattern = 'gvlVersion.Country.platform'.split('.')
  const gvlSpec3MigrationPattern = 'gvlSpec.gvlVersion.Country.platform'.split(
    '.'
  )
  const userMigratedToGvlVariant =
    molGvlVersion?.split('.').length === gvlSpec2MigrationPattern.length ||
    molGvlVersion?.split('.').length === gvlSpec3MigrationPattern.length

  const { gvlEnvironment, geo, useGeoDeviceVariantGVL = false } = clientConfig
  const migrateUserGvl =
    useGeoDeviceVariantGVL && !userMigratedToGvlVariant && existingUser

  const gvlVersion = userMigratedToGvlVariant
    ? setGvlVersionByMolGvlVersion(molGvlVersion)
    : newUser && useGeoDeviceVariantGVL
      ? setGvlVersionByClientConfig(_gvlVersion, geo, gvlEnvironment)
      : _gvlVersion

  const migratedGvlVersion =
    (!userMigratedToGvlVariant &&
      useGeoDeviceVariantGVL &&
      existingUser &&
      setGvlVersionByClientConfig(_gvlVersion, geo, gvlEnvironment)) ||
    undefined

  const gvlUiVersion =
    userMigratedToGvlVariant || useGeoDeviceVariantGVL
      ? setGvlVersionByClientConfig(_gvlUiVersion, geo, gvlEnvironment)
      : _gvlUiVersion

  return {
    gvlVersion,
    gvlUiVersion,
    migratedGvlVersion,
    migrateUserGvl
  }
}

/**
 * @param {OverrideableByStoredConfigItems} selectedTCDataItems
 * @param {ClientConfig} clientConfig
 * @param {boolean} existingUser
 */
export function getVendorListsAndMigrationVersions(
  selectedTCDataItems,
  clientConfig,
  existingUser
) {
  const { molGvlVersion } = selectedTCDataItems
  const {
    gvlVersion: _gvlVersion,
    gvlUiVersion: _gvlUiVersion,
    ...nonTCFVendorListVersions
  } = getVendorListVersions(selectedTCDataItems, clientConfig)

  const { gvlVersion, gvlUiVersion, migratedGvlVersion, migrateUserGvl } =
    applyMigrationConfig(
      clientConfig,
      molGvlVersion,
      existingUser,
      _gvlVersion,
      _gvlUiVersion
    )

  return {
    ...nonTCFVendorListVersions,
    gvlVersion,
    gvlUiVersion,
    migratedGvlVersion,
    migrateUserGvl
  }
}

/**
 * @typedef {Object} OverrideableByStoredConfigItems
 * @property {string|undefined} tcString
 * @property {string|undefined} addtlConsent
 * @property {string|number|undefined} cvlVersion
 * @property {string|undefined} nrvString
 * @property {string|number|undefined} nrvVersion
 * @property {string|undefined} molGvlVersion
 * @property {number} nextRepromptVersion
 * @property {number} repromptVersion
 */

/**
 * @typedef {import('./config').ClientConfig} ClientConfig
 */

/**
 * @typedef {import('@mailonline/tcf-core').TCModel} TCModel
 */
